import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/permissions";
import { toast } from "sonner";
import { Plus, Edit, Trash2, BookOpen, ArrowRight, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import ConfirmDeleteDialog from "@/components/admin/ConfirmDeleteDialog";
import { TIER_OPTIONS, TIER_BY_KEY } from "@/lib/pricing";

const DEFAULT_LIMITED = {
  limited_access_enabled: true,
  limited_access_days: 3,
  limited_access_question_limit: 20,
  limited_access_allow_review_mode: true,
  limited_access_allow_exam_mode: true,
  limited_access_allow_smart_review: false,
  limited_access_allow_ai_explanation: true,
  limited_access_allow_audio_mode: true,
};

export default function CoursesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ course_name: "", description: "", cover_image: "", status: "active", subscription_months: 6, price_tier: "", iap_product_id: "", is_free: false });
  const [limitedDialog, setLimitedDialog] = useState({ open: false, course: null, form: { ...DEFAULT_LIMITED } });
  const [limitedBusy, setLimitedBusy] = useState(false);
  const [deleteDlg, setDeleteDlg] = useState({ open: false, id: null, label: "", busy: false });

  const load = () => api.get("/courses").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => { setEditing(null); setForm({ course_name: "", description: "", cover_image: "", status: "active", subscription_months: 6, price_tier: "", iap_product_id: "", is_free: false }); };

  const openLimited = async (course) => {
    try {
      const r = await api.get(`/courses/${course.id}/limited-access`);
      setLimitedDialog({ open: true, course, form: { ...DEFAULT_LIMITED, ...r.data } });
    } catch {
      setLimitedDialog({ open: true, course, form: { ...DEFAULT_LIMITED } });
    }
  };

  const saveLimited = async () => {
    if (!limitedDialog.course) return;
    setLimitedBusy(true);
    try {
      await api.put(`/courses/${limitedDialog.course.id}/limited-access`, {
        ...limitedDialog.form,
        limited_access_days: Number(limitedDialog.form.limited_access_days) || 0,
        limited_access_question_limit: Number(limitedDialog.form.limited_access_question_limit) || 0,
      });
      toast.success("Limited access settings updated");
      setLimitedDialog({ open: false, course: null, form: { ...DEFAULT_LIMITED } });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLimitedBusy(false); }
  };

  const save = async () => {
    try {
      if (editing) { await api.patch(`/courses/${editing.id}`, form); toast.success("Updated"); }
      else { await api.post("/courses", form); toast.success("Created"); }
      setOpen(false); reset(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    setDeleteDlg((d) => ({ ...d, busy: true }));
    try {
      await api.delete(`/courses/${id}`);
      toast.success("Course deleted");
      setDeleteDlg({ open: false, id: null, label: "", busy: false });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete course");
      setDeleteDlg((d) => ({ ...d, busy: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="courses-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Content</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Courses</h1>
        </div>
        {hasPerm(user, "course", "add") && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="add-course-btn"><Plus className="w-4 h-4 mr-2" />New Course</Button></DialogTrigger>
            <DialogContent className="max-h-[85vh] flex flex-col p-0" data-testid="course-edit-dialog">
              <DialogHeader className="px-6 pt-6 pb-2 shrink-0"><DialogTitle>{editing ? "Edit Course" : "New Course"}</DialogTitle></DialogHeader>
              <div className="space-y-4 overflow-y-auto px-6 py-2 flex-1 min-h-0" data-testid="course-edit-scroll">
                <div><Label>Course name</Label><Input value={form.course_name} onChange={(e) => setForm({ ...form, course_name: e.target.value })} data-testid="course-name-input" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div><Label>Cover image URL</Label><Input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} /></div>
                <div>
                  <Label>Subscription duration (months)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="60"
                    value={form.subscription_months}
                    onChange={(e) => setForm({ ...form, subscription_months: Math.max(0, Math.min(60, parseInt(e.target.value || "0", 10))) })}
                    data-testid="course-subscription-months-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Applied when a student's payment is approved. <span className="font-semibold">0 = lifetime access</span> (no expiry). Default: 6 months.
                  </p>
                </div>
                <div className="flex items-start justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-900/20 dark:border-emerald-800 p-3">
                  <div>
                    <Label htmlFor="course-is-free">Free course</Label>
                    <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-0.5">
                      Every student enrolled (now or later) gets <b>full access immediately</b> — no trial limits, no payment, and no Subscribe button.
                    </p>
                  </div>
                  <Switch id="course-is-free" checked={!!form.is_free} onCheckedChange={(v) => setForm({ ...form, is_free: v })} data-testid="course-is-free-switch" />
                </div>
                <div className={form.is_free ? "opacity-50 pointer-events-none" : ""}>
                  <Label>Price Tier (6-month subscription)</Label>
                  <Select value={form.price_tier || "__free__"} onValueChange={(v) => setForm({ ...form, price_tier: v === "__free__" ? "" : v })}>
                    <SelectTrigger data-testid="course-price-tier-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIER_OPTIONS.map((o) => (
                        <SelectItem key={o.value || "__free__"} value={o.value || "__free__"}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Determines the price shown to students on iOS/Android/Web. Must match the tier product created in App Store Connect & Google Play (product ID <code className="font-mono">{TIER_BY_KEY[form.price_tier]?.product_id || "—"}</code>).
                  </p>
                </div>
                <div>
                  <Label>In-App Product ID (optional override)</Label>
                  <Input
                    value={form.iap_product_id}
                    onChange={(e) => setForm({ ...form, iap_product_id: e.target.value.trim() })}
                    placeholder="ph.prepmaster.reble_6mo"
                    data-testid="course-iap-product-id-input"
                    className="font-mono text-sm"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Paste the <b>Non-Renewing Subscription</b> product ID exactly as it appears in App Store Connect / Google Play (e.g. <code className="font-mono">ph.prepmaster.reble_6mo</code>). When set, this course uses its <b>own</b> per-course subscription instead of the shared tier product above. Leave empty to use the tier product. Price shown to the student still comes from the selected tier.
                  </p>
                </div>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter className="px-6 py-4 border-t shrink-0 bg-white"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={save} data-testid="course-save-btn">Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 ? <div className="text-slate-500 col-span-full">No courses yet. Create your first one.</div> :
          items.map((c) => (
            <Card key={c.id} className="p-6 border-slate-200 hover:shadow-md transition-shadow" data-testid={`course-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-md bg-blue-50 text-blue-700 grid place-items-center"><BookOpen className="w-5 h-5" strokeWidth={1.5} /></div>
                <Badge className={c.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-slate-200"}>{c.status}</Badge>
              </div>
              <h3 className="mt-4 font-display text-lg font-bold">{c.course_name}</h3>
              <p className="text-sm text-slate-600 mt-1 line-clamp-2">{c.description}</p>
              {c.pricing?.price_display && (
                <Badge variant="outline" className="mt-2 text-xs font-mono" data-testid={`course-${c.id}-price`}>
                  {c.pricing.price_display} · {c.pricing.months}mo · {c.pricing.label}
                </Badge>
              )}
              <div className="mt-5 flex items-center justify-between">
                <Link to={`/admin/courses/${c.id}/subjects`} className="text-sm font-semibold text-blue-700 inline-flex items-center gap-1 hover:underline" data-testid={`view-subjects-${c.id}`}>
                  View Subjects <ArrowRight className="w-4 h-4" />
                </Link>
                <div className="flex gap-2">
                  {user.role === "super_admin" && (
                    <Button size="icon" variant="outline" className="text-amber-700" onClick={() => openLimited(c)} data-testid={`limited-access-${c.id}`} title="Limited Access Settings">
                      <Lock className="w-4 h-4" />
                    </Button>
                  )}
                  {hasPerm(user, "course", "edit") && <Button size="icon" variant="outline" onClick={() => { setEditing(c); setForm({ course_name: c.course_name, description: c.description || "", cover_image: c.cover_image || "", status: c.status, subscription_months: (c.subscription_months ?? 6), price_tier: c.price_tier || "", iap_product_id: c.iap_product_id || "", is_free: !!c.is_free }); setOpen(true); }} data-testid={`edit-course-${c.id}`}><Edit className="w-4 h-4" /></Button>}
                  {hasPerm(user, "course", "delete") && <Button size="icon" variant="outline" className="text-red-600" onClick={() => setDeleteDlg({ open: true, id: c.id, label: c.course_name, busy: false })} data-testid={`del-course-${c.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
            </Card>
          ))}
      </div>

      {/* Limited Access Settings Dialog */}
      <Dialog open={limitedDialog.open} onOpenChange={(o) => setLimitedDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-lg" data-testid="limited-access-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" /> Limited Access Settings
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-500 -mt-2">
            Controls what a newly-approved student can access on <span className="font-semibold">{limitedDialog.course?.course_name}</span> before payment is permanently confirmed.
          </div>

          <div className="flex items-center justify-between gap-3 p-3 bg-slate-50 rounded-md border border-slate-200">
            <div>
              <div className="text-sm font-semibold">Enable Limited Access</div>
              <div className="text-[11px] text-slate-500">When OFF, approval grants Full Access immediately.</div>
            </div>
            <Switch
              checked={!!limitedDialog.form.limited_access_enabled}
              onCheckedChange={(v) => setLimitedDialog((d) => ({ ...d, form: { ...d.form, limited_access_enabled: v } }))}
              data-testid="la-enabled-toggle"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="la_days">Duration (days)</Label>
              <Input id="la_days" type="number" min={0} max={90}
                value={limitedDialog.form.limited_access_days}
                onChange={(e) => setLimitedDialog((d) => ({ ...d, form: { ...d.form, limited_access_days: e.target.value } }))}
                data-testid="la-days-input" />
            </div>
            <div>
              <Label htmlFor="la_qlim">Question limit</Label>
              <Input id="la_qlim" type="number" min={0} max={10000}
                value={limitedDialog.form.limited_access_question_limit}
                onChange={(e) => setLimitedDialog((d) => ({ ...d, form: { ...d.form, limited_access_question_limit: e.target.value } }))}
                data-testid="la-qlimit-input" />
            </div>
          </div>

          <div className="space-y-2 mt-1">
            {[
              ["limited_access_allow_review_mode", "Allow Review Mode"],
              ["limited_access_allow_exam_mode", "Allow Exam Mode"],
              ["limited_access_allow_smart_review", "Allow Smart Review"],
              ["limited_access_allow_ai_explanation", "Allow AI Explanation"],
              ["limited_access_allow_audio_mode", "Allow Audio Study Mode"],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between gap-3 p-2 rounded-md border border-slate-100">
                <Label className="text-sm">{label}</Label>
                <Switch
                  checked={!!limitedDialog.form[key]}
                  onCheckedChange={(v) => setLimitedDialog((d) => ({ ...d, form: { ...d.form, [key]: v } }))}
                  data-testid={`la-${key}`}
                />
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setLimitedDialog({ open: false, course: null, form: { ...DEFAULT_LIMITED } })}>Cancel</Button>
            <Button onClick={saveLimited} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" disabled={limitedBusy} data-testid="la-save-btn">
              {limitedBusy ? "Saving…" : "Save Settings"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteDlg.open}
        onCancel={() => setDeleteDlg({ open: false, id: null, label: "", busy: false })}
        onConfirm={() => del(deleteDlg.id)}
        title="Delete this course?"
        itemLabel={deleteDlg.label}
        description="will be permanently removed along with ALL of its subjects, modules, questions, and AI explanations."
        busy={deleteDlg.busy}
        confirmLabel="Delete course"
        dataTestId="delete-course"
      />
    </div>
  );
}
